import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { getDefaultListColor } from '../utils/colorUtils';
import { TodoList } from '../types/todo';
import { Users, Lock, Share2 } from 'lucide-react';
import { ListSharingSelector } from './ListSharingSelector';
import { ColorPicker } from './ColorPicker';
import './CreateListForm.css'; // Reuse the same CSS

interface EditListFormProps {
  list: TodoList;
  onSave?: (data: { name: string; description?: string; color?: string; isShared?: boolean; sharedWith?: string[]; isPublic?: boolean }) => void;
  onClose?: () => void;
}

export const EditListForm: React.FC<EditListFormProps> = ({ 
  list, 
  onSave,
  onClose
}) => {
  const { currentUser } = useUser();
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description || '');
  const [selectedColor, setSelectedColor] = useState(list.color || getDefaultListColor().value);
  const [isShared, setIsShared] = useState(list.isShared || false);
  const [sharedWith, setSharedWith] = useState<string[]>(list.sharedWith || []);
  const [isPublic, setIsPublic] = useState(list.isPublic || false);

  const isOwner = currentUser?.email === list.createdBy;
  
  const hasSharingChanges = isShared !== list.isShared ||
    JSON.stringify(sharedWith.sort()) !== JSON.stringify((list.sharedWith || []).sort()) ||
    isPublic !== (list.isPublic || false);

  // Expose current form data to parent
  const getCurrentData = () => ({
    name: name.trim(),
    description: description.trim() || undefined,
    color: selectedColor,
    isShared,
    sharedWith,
    isPublic,
  });

  // Call onSave callback when form data changes
  useEffect(() => {
    if (onSave) {
      onSave(getCurrentData());
    }
  }, [name, description, selectedColor, isShared, sharedWith, isPublic, onSave]);

  useEffect(() => {
    setName(list.name);
    setDescription(list.description || '');
    setSelectedColor(list.color || getDefaultListColor().value);
    setIsShared(list.isShared || false);
    setSharedWith(list.sharedWith || []);
    setIsPublic(list.isPublic || false);
  }, [list]);

  const handleSharingChange = (newIsShared: boolean, newSharedWith: string[]) => {
    // Just update local state - don't make API calls until save
    setIsShared(newIsShared);
    setSharedWith(newSharedWith);
  };

  const handleMakePrivate = () => {
    // Just update local state - don't make API calls until save
    setIsShared(false);
    setSharedWith([]);
  };

  return (
    <div className="create-list-form">
      <div className="list-form">
        <div className="form-group">
          <input
            type="text"
            placeholder="List name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="list-input"
            autoFocus={window.innerWidth > 768}
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <textarea
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="list-textarea"
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Sharing Section - Only show if user is the owner */}
        {isOwner && (
          <div className="form-group">
            <div className="sharing-section">
              <div className="sharing-header">
                <div className="sharing-status">
                  {isShared ? (
                    <div className="shared-indicator">
                      <Share2 size={16} />
                      <span>Shared with {sharedWith.length} user{sharedWith.length !== 1 ? 's' : ''}</span>
                    </div>
                  ) : (
                    <div className="private-indicator">
                      <Lock size={16} />
                      <span>Private list</span>
                    </div>
                  )}
                </div>
                {isShared && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleMakePrivate}
                  >
                    Make Private
                  </button>
                )}
              </div>
              
              <ListSharingSelector
                sharedWith={sharedWith}
                onChange={(userEmail, action) => {
                  if (action === 'add') {
                    handleSharingChange(true, [...sharedWith, userEmail]);
                  } else {
                    const newSharedWith = sharedWith.filter(email => email !== userEmail);
                    handleSharingChange(newSharedWith.length > 0, newSharedWith);
                  }
                }}
                onClose={onClose}
              />
            </div>
          </div>
        )}

        {/* Super Share Section */}
        {isOwner && (
          <div className="form-group">
            <div className="super-share-section">
              <div className="super-share-header">
                <div className="super-share-title">
                  <h4>🚀 Super Share</h4>
                  <div className={`super-share-status ${isPublic ? 'status-public' : 'status-private'}`}>
                    {isPublic ? (
                      <>
                        <Share2 size={14} />
                        <span>Public</span>
                      </>
                    ) : (
                      <>
                        <Lock size={14} />
                        <span>Private</span>
                      </>
                    )}
                  </div>
                </div>
                <p>
                  {isPublic 
                    ? "This list is publicly accessible with a readonly link"
                    : "Make this list publicly accessible with a readonly link"
                  }
                </p>
              </div>
              
              <div className="super-share-controls">
                <div className="super-share-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <div className="toggle-label">
                    <span className="toggle-primary">
                      {isPublic ? 'Public sharing enabled' : 'Enable public sharing'}
                    </span>
                    <span className="toggle-secondary">
                      {isPublic 
                        ? 'Anyone with the link can view this list'
                        : 'Generate a readonly link for public access'
                      }
                    </span>
                  </div>
                </div>

                {isPublic && list.publicAccessToken && (
                  <div className="public-url-section">
                    <label>Public URL:</label>
                    <div className="public-url-display">
                      <input
                        type="text"
                        value={`${window.location.origin}/public/${list.publicAccessToken}`}
                        readOnly
                        className="public-url-input"
                        onClick={(e) => e.currentTarget.select()}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/public/${list.publicAccessToken}`);
                        }}
                        title="Copy link"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {isPublic && !list.publicAccessToken && (
                  <div className="public-url-pending">
                    <Lock size={16} />
                    <span>Public URL will be generated after saving</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Read-only sharing info for non-owners */}
        {!isOwner && isShared && (
          <div className="form-group">
            <div className="sharing-info">
              <Users size={16} />
              <span>This list is shared by {list.createdBy}</span>
              {sharedWith.length > 0 && (
                <span className="shared-count">
                  • {sharedWith.length + 1} member{sharedWith.length !== 0 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        <ColorPicker 
          selectedColor={selectedColor}
          onColorChange={setSelectedColor}
          showComment={true}
        />

        {/* Pending Changes Indicator */}
        {hasSharingChanges && (
          <div className="pending-changes-indicator">
            <div className="pending-changes-content">
              <div className="pending-icon">⏳</div>
              <div className="pending-text">
                <span className="pending-title">Unsaved changes</span>
                <span className="pending-description">
                  Your sharing settings have been modified. Click "Save" to apply changes.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
